/**
 * Générateur — Propriétés de la symétrie axiale et construction de symétriques
 * Attendu ID : 673 | Niveau : 6ème | Domaine : Espace et géométrie
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - nature_figure_image_qcm                : QCM 4 choix (nature du symétrique)
 *   - conservation_longueur_saisie           : saisie (L1'L2' = L1L2)
 *   - conservation_proprietes_vf             : V/F 3V + 3F (longueurs, angles, alignement)
 *   - etapes_construction_triangle_ordonner  : 3 étapes à ordonner (construction triangle)
 *   - association_nature_mesure_relier       : 4 paires (figure ↔ nature+mesure image)
 *   - cercle_image_qcm                       : QCM 4 choix (centre et rayon du cercle image)
 *
 * Spécificité : 100 % texte pur. Pool LETTRES identique à gen_672 (23 lettres,
 * I exclu). Les figures restent des descriptions textuelles (pas de SVG).
 *
 * Partition avec 672 :
 *   - 672 : définition du symétrique d'un POINT, involutivité.
 *   - 673 : propriétés de conservation (longueurs, angles, alignements) et
 *           construction de symétriques de FIGURES (ce générateur).
 */

import {
    randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeOrdonner,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 673;
const LETTRES = 'ABCDEFGHJKLMNOPQRSTUVWYZ'.split('').filter(c => c !== 'I');
const AXES_POOL = ['(d)', '(Δ)'];

const L_NATURE = 'Nature du symétrique d\'une figure (QCM)';
const L_LONG   = 'Conservation de longueur (saisie)';
const L_VF     = 'Conservation par symétrie axiale (V/F)';
const L_ETAPES = 'Étapes de construction du symétrique d\'un triangle (ordering)';
const L_RELIER = 'Association figure ↔ nature et mesure de l\'image (relier)';
const L_CERCLE = 'Centre et rayon du cercle image (QCM)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_NATURE,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

const fr = (x) => {
    if (Number.isInteger(x)) return String(x);
    return String(x).replace('.', ',');
};

// ─── Pools numériques ────────────────────────────────────────

const LONGUEURS_POOL = [3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10];
const RAYONS_POOL    = [2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8];
const ANGLES_POOL    = [15, 30, 45, 60, 75, 90, 105, 120];

// ─── Format 1 — Nature du symétrique (QCM) ───────────────────

const genNatureFigureImage = () => {
    const [L1, L2, L3, O] = pickLetters(4); // O tiré AVEC les autres → filtre anti-gros-mots global
    const r = randomChoice(RAYONS_POOL);
    const ax = randomChoice(AXES_POOL);
    const sub = Math.floor(Math.random() * 4);

    let choix, bonne, texte;

    if (sub === 0) {
        bonne = `Un segment de même longueur que [${L1}${L2}].`;
        choix = [
            bonne,
            'Une demi-droite.',
            'Une droite.',
            `Un segment de longueur double de [${L1}${L2}].`,
        ];
        texte = `<p>Quelle est la nature du symétrique d'un segment [${L1}${L2}] par rapport à une droite ${ax} ?</p>`;
    } else if (sub === 1) {
        // Toutes les options chiffrées portent la MÊME valeur que l'énoncé : le
        // nombre ne désigne plus la bonne réponse. « Une ellipse » est retirée,
        // aucun élève de 6ème n'ayant rencontré ce mot — le distracteur ne
        // portait aucune erreur possible.
        bonne = `Un cercle de rayon ${fr(r)} cm.`;
        choix = [
            bonne,
            `Un cercle de rayon ${fr(r)} cm, mais seulement si le centre est sur ${ax}.`,
            `Un cercle de diamètre ${fr(r)} cm.`,
            // Exactement la même longueur que la bonne réponse, à la lettre
            // près : sans elle, « la plus courte » désignait la bonne à coup sûr.
            // La confusion cercle / disque est par ailleurs bien réelle.
            `Un disque de rayon ${fr(r)} cm.`,
        ];
        texte = `<p>Quelle est la nature du symétrique du cercle de centre ${O} et de rayon ${fr(r)} cm par rapport à une droite ${ax} ?</p>`;
    } else if (sub === 2) {
        // La bonne réponse était deux fois plus longue que les trois autres :
        // on la raccourcit et on allonge un distracteur. « Un quadrilatère »
        // disparaît, personne ne croit qu'un triangle en devienne un.
        bonne = 'Un triangle de mêmes longueurs et de mêmes angles.';
        choix = [
            bonne,
            `Un triangle de mêmes angles, mais dont les côtés sont réduits de moitié.`,
            `Un triangle équilatéral, quelle que soit la nature de ${L1}${L2}${L3}.`,
            `Un triangle plus petit que ${L1}${L2}${L3}.`,
        ];
        texte = `<p>Quelle est la nature du symétrique d'un triangle ${L1}${L2}${L3} par rapport à une droite ${ax} ?</p>`;
    } else {
        // « Une droite » était la seule option à reprendre le mot de la
        // question : un deuxième la reprend désormais, avec une restriction
        // fausse mais plausible.
        bonne = 'Une droite.';
        choix = [
            bonne,
            `Une droite, mais seulement si elle coupe ${ax}.`,
            'Une demi-droite.',
            'Un segment.',
        ];
        texte = `<p>Quelle est la nature du symétrique de la droite (${L1}${L2}) par rapport à une droite ${ax} ?</p>`;
    }

    const payload = makeQcm(choix, bonne, texte, opts(L_NATURE));
    payload._meta.generatorKey = 'nature_figure_image_qcm';
    return payload;
};

// ─── Format 2 — Conservation de longueur (saisie) ────────────

const genConservationLongueurSaisie = () => {
    const [L1, L2] = pickLetters(2);
    const len = randomChoice(LONGUEURS_POOL);
    const ax = randomChoice(AXES_POOL);

    let texte;
    if (Math.random() < 0.5) {
        texte = `<p>Le segment [${L1}${L2}] mesure ${fr(len)} cm. Quelle est la longueur de son symétrique [${L1}'${L2}'] par rapport à ${ax} (en cm) ?</p>`;
    } else {
        texte = `<p>[${L1}'${L2}'] est le symétrique de [${L1}${L2}] par rapport à ${ax}. Si ${L1}'${L2}' = ${fr(len)} cm, quelle est la longueur ${L1}${L2} (en cm) ?</p>`;
    }

    const payload = makeSaisie(fr(len), texte, {
        ...opts(L_LONG),
        suffixe: ' cm',
        strict: false,
    });
    payload._meta.generatorKey = 'conservation_longueur_saisie';
    return payload;
};

// ─── Format 3 — Conservation (V/F) ───────────────────────────

const buildAffirmationsVF = () => {
    const [L1, L2, L3] = pickLetters(3);
    return {
        VRAIS: {
            V1: () => ({
                id: 'V1', verite: 0,
                texte: 'La symétrie axiale conserve les longueurs : le symétrique d\'un segment a la même longueur que le segment original.',
            }),
            V2: () => ({
                id: 'V2', verite: 0,
                texte: 'La symétrie axiale conserve les mesures des angles.',
            }),
            V3: () => ({
                id: 'V3', verite: 0,
                texte: 'Le symétrique d\'un cercle par rapport à une droite est un cercle de même rayon.',
            }),
            V4: () => ({
                id: 'V4', verite: 0,
                texte: `Le symétrique du triangle ${L1}${L2}${L3} est un triangle qui a les mêmes longueurs de côtés et les mêmes mesures d'angles que ${L1}${L2}${L3}.`,
            }),
            V5: () => ({
                id: 'V5', verite: 0,
                texte: 'La symétrie axiale conserve l\'alignement des points.',
            }),
        },
        FAUX: {
            F1: () => ({
                id: 'F1', verite: 1,
                texte: 'La symétrie axiale double les longueurs.',
            }),
            F2: () => ({
                id: 'F2', verite: 1,
                texte: 'Le symétrique d\'un cercle de rayon 5 cm est un cercle de rayon 10 cm.',
            }),
            F3: () => ({
                id: 'F3', verite: 1,
                texte: `Le symétrique d'un triangle ${L1}${L2}${L3} est toujours un triangle équilatéral, quelle que soit la nature du triangle d'origine.`,
            }),
            F4: () => ({
                id: 'F4', verite: 1,
                texte: 'La symétrie axiale change la nature d\'un segment en une droite.',
            }),
            F5: () => ({
                id: 'F5', verite: 1,
                texte: `Le symétrique d'une droite (${L1}${L2}) par rapport à (d) est toujours parallèle à (${L1}${L2}).`,
            }),
        },
    };
};

const genConservationVF = () => {
    let tries = 0;
    while (tries < 5) {
        const builders = buildAffirmationsVF();
        const idsV = randomChoiceMultiple(Object.keys(builders.VRAIS), 3);
        const idsF = randomChoiceMultiple(Object.keys(builders.FAUX), 3);
        const vrais = idsV.map(id => builders.VRAIS[id]());
        const faux  = idsF.map(id => builders.FAUX[id]());
        const mixed = shuffle([...vrais, ...faux]);

        const lignes = mixed.map(a => a.texte);
        const bonnesReponses = mixed.map(a => a.verite);

        if (new Set(lignes).size === lignes.length) {
            const texte = `<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
            payload._meta.generatorKey = 'conservation_proprietes_vf';
            return payload;
        }
        tries++;
    }

    const builders = buildAffirmationsVF();
    const vrais = ['V1', 'V2', 'V4'].map(id => builders.VRAIS[id]());
    const faux  = ['F1', 'F3', 'F5'].map(id => builders.FAUX[id]());
    const mixed = shuffle([...vrais, ...faux]);
    const lignes = mixed.map(a => a.texte);
    const bonnesReponses = mixed.map(a => a.verite);
    const texte = `<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
    payload._meta.generatorKey = 'conservation_proprietes_vf';
    return payload;
};

// ─── Format 4 — Étapes construction triangle (ordering) ──────

const genEtapesConstructionTriangle = () => {
    const [L1, L2, L3] = pickLetters(3);
    const ax = randomChoice(AXES_POOL);

    const items = [
        `Identifier les trois sommets ${L1}, ${L2}, ${L3} du triangle.`,
        `Construire les symétriques ${L1}', ${L2}' et ${L3}' des trois sommets par rapport à ${ax}.`,
        `Tracer les trois segments [${L1}'${L2}'], [${L2}'${L3}'] et [${L1}'${L3}'] pour obtenir le triangle symétrique.`,
    ];

    const texte = `<p>Voici les étapes pour construire le symétrique du triangle ${L1}${L2}${L3} par rapport à la droite ${ax}. Remets-les dans le bon ordre.</p>`;

    const payload = makeOrdonner(items, texte, opts(L_ETAPES));
    payload._meta.generatorKey = 'etapes_construction_triangle_ordonner';
    return payload;
};

// ─── Format 5 — Association figure ↔ image (relier) ──────────

/** Dédup numérique des candidats leurres contre les mesures conservées du
 * tirage (et entre eux) — jamais de leurre égal à une mesure correcte. */
const construireLeurresMesure = (candidats, mesuresPrises, rendu) => {
    const prises = new Set(mesuresPrises);
    const out = [];
    for (const c of candidats) {
        if (out.length >= 2) break;
        if (prises.has(c.v)) continue;
        prises.add(c.v);
        out.push({ target: rendu(c.v), code: c.code });
    }
    return out;
};

const genAssociationRelier = () => {
    // Refonte bilan prof D3 (2026-07-27) : l'ancien tirage mélangeait les
    // types d'objets → chaque cible répétait le mot-catégorie de sa source
    // (« Segment [WL]… » → « Segment de longueur 8 cm »), appariement par le
    // mot sans jamais utiliser la conservation des mesures. Désormais chaque
    // tirage porte sur UNE SEULE famille (segments, cercles ou angles) : les
    // cibles sont toutes du même type et ne se distinguent que par la mesure —
    // relier exige d'appliquer « l'image a la même mesure ». Les leurres sont
    // les mesures que donnerait un élève qui croit que la symétrie transforme
    // la mesure : double (il compte figure + image, l'aller-retour par l'axe),
    // moitie (il partage au lieu de conserver), diametre (rayon confondu avec
    // le diamètre), supplement (180° − a : il transfère l'inversion
    // d'orientation sur la mesure).
    const famille = randomChoice(['segments', 'cercles', 'angles']);
    let pairs, leurres;

    if (famille === 'segments') {
        const [A, B, C, D] = pickLetters(4);
        const [l1, l2] = shuffle(LONGUEURS_POOL).slice(0, 2);
        pairs = [
            { source: `Segment [${A}${B}] de longueur ${fr(l1)} cm`, target: `Segment de longueur ${fr(l1)} cm` },
            { source: `Segment [${C}${D}] de longueur ${fr(l2)} cm`, target: `Segment de longueur ${fr(l2)} cm` },
        ];
        leurres = construireLeurresMesure(
            [{ v: 2 * l1, code: 'double' }, { v: l2 / 2, code: 'moitie' },
             { v: 2 * l2, code: 'double' }, { v: l1 / 2, code: 'moitie' }],
            [l1, l2], (v) => `Segment de longueur ${fr(v)} cm`);
    } else if (famille === 'cercles') {
        const [O1, O2] = pickLetters(2);
        const [r1, r2] = shuffle(RAYONS_POOL).slice(0, 2);
        pairs = [
            { source: `Cercle de centre ${O1} et de rayon ${fr(r1)} cm`, target: `Cercle de rayon ${fr(r1)} cm` },
            { source: `Cercle de centre ${O2} et de rayon ${fr(r2)} cm`, target: `Cercle de rayon ${fr(r2)} cm` },
        ];
        leurres = construireLeurresMesure(
            [{ v: 2 * r1, code: 'diametre' }, { v: r2 / 2, code: 'moitie' },
             { v: 2 * r2, code: 'diametre' }, { v: r1 / 2, code: 'moitie' }],
            [r1, r2], (v) => `Cercle de rayon ${fr(v)} cm`);
    } else {
        const [A, B, C, D, E, F] = pickLetters(6);
        const [a1, a2] = shuffle(ANGLES_POOL).slice(0, 2);
        pairs = [
            { source: `Angle $\\widehat{${B}${A}${C}}$ de mesure ${a1}°`, target: `Angle de mesure ${a1}°` },
            { source: `Angle $\\widehat{${E}${D}${F}}$ de mesure ${a2}°`, target: `Angle de mesure ${a2}°` },
        ];
        leurres = construireLeurresMesure(
            [{ v: 180 - a1, code: 'supplement' }, { v: 180 - a2, code: 'supplement' },
             { v: a1 / 2, code: 'moitie' }, { v: a2 / 2, code: 'moitie' }],
            [a1, a2], (v) => `Angle de mesure ${fr(v)}°`);
    }

    const texte = '<p>Associe chaque figure à la description de son symétrique par rapport à une droite (d). '
        + 'Attention : certaines descriptions ne correspondent à aucune figure.</p>';

    const payload = makeRelier(pairs, texte, { ...opts(L_RELIER), cibles_leurres: leurres });
    payload._meta.generatorKey = 'association_nature_mesure_relier';
    payload._meta.params = {
        famille,
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// ─── Format 6 — Cercle image (QCM) ───────────────────────────

const genCercleImageQcm = () => {
    const [L1, L2, O] = pickLetters(3); // O tiré AVEC les autres → filtre anti-gros-mots global
    const r = randomChoice(RAYONS_POOL);
    const ax = randomChoice(AXES_POOL);

    let choix, bonne, texte;

    if (Math.random() < 0.5) {
        // V1 — cercle image complet
        bonne = `Le cercle de centre ${O}' (symétrique de ${O} par rapport à ${ax}) et de rayon ${fr(r)} cm.`;
        choix = [
            bonne,
            `Le cercle de centre ${O} (inchangé) et de rayon ${fr(r)} cm.`,
            `Le cercle de centre ${O}' et de rayon ${fr(2 * r)} cm.`,
            `Le cercle de centre ${O}' et de rayon ${fr(r / 2)} cm.`,
        ];
        texte = `<p>On considère un point ${L1} hors de la droite ${ax}. Le cercle de centre ${O} et de rayon ${fr(r)} cm est envoyé par la symétrie d'axe ${ax} sur quel cercle ?</p>`;
    } else {
        // V2 — rayon du cercle image
        bonne = `${fr(r)} cm`;
        choix = [
            bonne,
            `${fr(2 * r)} cm`,
            `${fr(r / 2)} cm`,
            `Cela dépend de la droite ${ax}.`,
        ];
        texte = `<p>On considère un point ${L2} dans le plan. Le symétrique par rapport à ${ax} d'un cercle de centre ${O} et de rayon ${fr(r)} cm a pour rayon :</p>`;
    }

    const payload = makeQcm(choix, bonne, texte, opts(L_CERCLE));
    payload._meta.generatorKey = 'cercle_image_qcm';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    nature_figure_image_qcm:                genNatureFigureImage,
    conservation_longueur_saisie:           genConservationLongueurSaisie,
    conservation_proprietes_vf:             genConservationVF,
    etapes_construction_triangle_ordonner:  genEtapesConstructionTriangle,
    association_nature_mesure_relier:       genAssociationRelier,
    cercle_image_qcm:                       genCercleImageQcm,
};

export const CATEGORIES = [
    {
        id: 'proprietes_conservation_symetrie',
        label: 'Propriétés de conservation de la symétrie axiale',
        formatsList: [
            { key: 'nature_figure_image_qcm',          label: 'QCM' },
            { key: 'conservation_longueur_saisie',     label: 'Saisie' },
            { key: 'conservation_proprietes_vf',       label: 'V/F' },
            { key: 'association_nature_mesure_relier', label: 'Relier' },
        ],
    },
    {
        id: 'construction_symetrique_figure',
        label: 'Construire le symétrique d\'une figure',
        formatsList: [
            { key: 'etapes_construction_triangle_ordonner', label: 'Ordonner' },
        ],
    },
    {
        id: 'cercle_image_symetrie',
        label: 'Image d\'un cercle par symétrie axiale',
        formatsList: [
            { key: 'cercle_image_qcm', label: 'QCM' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    LETTRES,
    LONGUEURS_POOL,
    RAYONS_POOL,
    ANGLES_POOL,
    pickLetters,
    buildAffirmationsVF,
    genNatureFigureImage,
    genConservationLongueurSaisie,
    genConservationVF,
    genEtapesConstructionTriangle,
    genAssociationRelier,
    genCercleImageQcm,
};
